import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import LinearProgress from '@mui/material/LinearProgress';
import AdminLoading from './AdminLoading';
import AdminEmptyState from './AdminEmptyState';
import toast from 'react-hot-toast';
import { IoCalendarOutline, IoTrashOutline, IoPencilOutline, IoAddOutline, IoImageOutline, IoAlertCircleOutline, IoCheckmarkCircleOutline, IoCloudUploadOutline } from 'react-icons/io5';
import { Link } from 'react-router-dom';
import { uploadImage, API_BASE, getToken } from '../../api/client';
import { INDIAN_FESTIVALS, TYPE_COLORS, TYPE_LABELS, PRIORITY_COLORS, daysUntil, getUpcomingFestivals } from './festivalData';

const EMPTY_FORM = {
  name: '',
  context_key: '',
  description: '',
  start_date: '',
  end_date: '',
  active: false,
};

const FESTIVAL_SUGGESTIONS = [
  { name: 'Diwali', key: 'diwali' },
  { name: 'Navratri', key: 'navratri' },
  { name: 'Holi', key: 'holi' },
  { name: 'Eid', key: 'eid' },
  { name: 'Christmas', key: 'christmas' },
  { name: 'Wedding Season', key: 'wedding-season' },
  { name: 'Durga Puja', key: 'durga-puja' },
  { name: 'Onam', key: 'onam' },
  { name: 'Karva Chauth', key: 'karva-chauth' },
  { name: 'Ganesh Chaturthi', key: 'ganesh-chaturthi' },
];

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function isCurrentlyActive(row) {
  if (row.active === 1 || row.active === true) return true;
  const today = new Date().toISOString().slice(0, 10);
  return row.start_date <= today && row.end_date >= today;
}

function QuickSliderDialog({ open, festival, onClose, onSaved }) {
  const [imageUrl, setImageUrl] = useState('');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = React.useRef();

  React.useEffect(() => {
    if (open && festival) {
      setTitle(festival.name);
      setSubtitle('');
      setImageUrl('');
    }
  }, [open, festival]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setImageUrl(url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!imageUrl) { toast.error('Please upload or paste an image URL'); return; }
    setSaving(true);
    try {
      await api('/api/admin/hero-sliders', {
        method: 'POST',
        body: JSON.stringify({
          context: 'shop',
          context_key: festival.suggestedKey,
          slides: [{ image_url: imageUrl, title: title.trim(), subtitle: subtitle.trim(), cta_text: 'Shop Now', cta_url: '/festive-vibes' }],
        }),
      });
      toast.success(`Slider added for ${festival.name}!`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!festival) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Add hero slider — {festival.name}
        <Typography variant="caption" display="block" color="text.secondary">
          Context key: <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>{festival.suggestedKey}</code>
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
        {/* Image upload */}
        <Box>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Slider image *</Typography>
          {imageUrl && (
            <Box sx={{ mb: 1.5, borderRadius: 2, overflow: 'hidden', maxHeight: 180 }}>
              <img src={imageUrl.startsWith('http') ? imageUrl : `${API_BASE}${imageUrl}`} alt="preview" style={{ width: '100%', height: 180, objectFit: 'cover' }} />
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<IoCloudUploadOutline />} onClick={() => fileRef.current?.click()} disabled={uploading} size="small">
              {uploading ? 'Uploading…' : 'Upload image'}
            </Button>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
          </Box>
          <TextField
            fullWidth size="small" label="Or paste image URL" value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            sx={{ mt: 1.5 }} placeholder="https://…"
          />
        </Box>
        <TextField fullWidth size="small" label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <TextField fullWidth size="small" label="Subtitle (optional)" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
        <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
          This adds one slide. To add more slides or edit, go to{' '}
          <Link to="/admin/pages/hero-sliders" style={{ color: 'inherit', fontWeight: 600 }}>Hero Sliders</Link>.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || uploading}>
          {saving ? 'Saving…' : 'Save slider'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function FestivalCalendarView({ schedules, onSchedulesRefresh }) {
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()));
  const [typeFilter, setTypeFilter] = useState('all');
  const [quickSlider, setQuickSlider] = useState(null);

  const scheduledKeys = useMemo(() => new Set((schedules || []).map((s) => s.context_key)), [schedules]);

  const festivals = useMemo(() => {
    return INDIAN_FESTIVALS
      .filter((f) => f.date.startsWith(yearFilter))
      .filter((f) => typeFilter === 'all' || f.type === typeFilter)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [yearFilter, typeFilter]);

  const years = ['2026', '2027'];

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3, alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>Year:</Typography>
        {years.map((y) => (
          <Chip key={y} label={y} onClick={() => setYearFilter(y)}
            variant={yearFilter === y ? 'filled' : 'outlined'}
            color={yearFilter === y ? 'primary' : 'default'} size="small" />
        ))}
        <Box sx={{ mx: 1, borderLeft: '1px solid', borderColor: 'divider', height: 20 }} />
        <Typography variant="body2" color="text.secondary" fontWeight={500}>Type:</Typography>
        {['all', 'hindu', 'muslim', 'christian', 'national', 'season'].map((t) => (
          <Chip key={t} label={t === 'all' ? 'All' : TYPE_LABELS[t]}
            onClick={() => setTypeFilter(t)}
            variant={typeFilter === t ? 'filled' : 'outlined'}
            color={typeFilter === t ? 'secondary' : 'default'} size="small" />
        ))}
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IoCheckmarkCircleOutline color="#388e3c" /><Typography variant="caption" color="text.secondary">Slider scheduled</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IoAlertCircleOutline color="#d32f2f" /><Typography variant="caption" color="text.secondary">Needs slider</Typography>
        </Box>
        {Object.entries(PRIORITY_COLORS).map(([p, c]) => (
          <Box key={p} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c }} />
            <Typography variant="caption" color="text.secondary">{p} priority</Typography>
          </Box>
        ))}
      </Box>

      {/* Festival cards grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
        {festivals.map((f, i) => {
          const days = daysUntil(f.date);
          const isPast = days < 0;
          const isScheduled = scheduledKeys.has(f.suggestedKey);
          const needsPrep = !isPast && days <= f.prepDays && !isScheduled;
          const tc = TYPE_COLORS[f.type] || TYPE_COLORS.hindu;
          return (
            <Card key={i} variant="outlined" sx={{
              borderLeft: `4px solid ${tc.border}`,
              opacity: isPast ? 0.5 : 1,
              bgcolor: needsPrep ? '#fff8f0' : 'background.paper',
              position: 'relative',
            }}>
              <CardContent sx={{ pb: '12px !important' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PRIORITY_COLORS[f.priority], flexShrink: 0 }} />
                    <Typography variant="body2" fontWeight={700}>{f.name}</Typography>
                  </Box>
                  {isScheduled
                    ? <Tooltip title="Hero slider is set up"><Box><IoCheckmarkCircleOutline color="#388e3c" size={18} /></Box></Tooltip>
                    : <Tooltip title="No hero slider yet"><Box><IoAlertCircleOutline color={needsPrep ? '#d32f2f' : '#bbb'} size={18} /></Box></Tooltip>
                  }
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, ml: 2 }}>
                  {new Date(f.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mt: 1, ml: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Chip label={TYPE_LABELS[f.type]} size="small"
                    sx={{ bgcolor: tc.bg, color: tc.color, fontSize: '0.7rem', height: 20 }} />
                  {!isPast && (
                    <Typography variant="caption" fontWeight={600}
                      sx={{ color: days <= 7 ? '#d32f2f' : days <= 30 ? '#f57c00' : 'text.secondary' }}>
                      {days === 0 ? 'Today!' : `${days} days away`}
                    </Typography>
                  )}
                  {isPast && <Typography variant="caption" color="text.disabled">Passed</Typography>}
                </Box>

                {needsPrep && (
                  <Alert severity="warning" sx={{ mt: 1, py: 0, fontSize: '0.72rem' }}>
                    Prepare in {days} days — upload hero slider now!
                  </Alert>
                )}

                {!isPast && !isScheduled && (
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.max(0, Math.min(100, ((f.prepDays - days) / f.prepDays) * 100))}
                      color={days <= f.prepDays ? 'warning' : 'inherit'}
                      sx={{ height: 3, borderRadius: 2 }}
                    />
                    <Typography variant="caption" color="text.disabled">
                      Suggested prep: {f.prepDays} days before
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  {isScheduled ? (
                    <Button component={Link} to="/admin/pages/hero-sliders" size="small"
                      startIcon={<IoImageOutline />} variant="outlined"
                      sx={{ fontSize: '0.72rem', py: 0.25 }}>
                      Edit slider
                    </Button>
                  ) : (
                    <Button size="small" startIcon={<IoImageOutline />} variant="contained"
                      color="primary" sx={{ fontSize: '0.72rem', py: 0.25 }}
                      onClick={() => setQuickSlider(f)}>
                      Add slider
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {festivals.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No festivals found for selected filters.
        </Typography>
      )}

      <QuickSliderDialog
        open={!!quickSlider}
        festival={quickSlider}
        onClose={() => setQuickSlider(null)}
        onSaved={() => { onSchedulesRefresh?.(); }}
      />
    </Box>
  );
}

export default function AdminFestivalSchedule() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/api/admin/festival-schedules');
      setSchedules(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Failed to load festival schedules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || '',
      context_key: row.context_key || '',
      description: row.description || '',
      start_date: row.start_date ? String(row.start_date).slice(0, 10) : '',
      end_date: row.end_date ? String(row.end_date).slice(0, 10) : '',
      active: row.active === 1 || row.active === true,
    });
    setDialogOpen(true);
  };

  const handleSuggestion = (s) => {
    setForm((f) => ({ ...f, name: s.name, context_key: s.key }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.context_key.trim()) return toast.error('Context key is required');
    if (!form.start_date) return toast.error('Start date is required');
    if (!form.end_date) return toast.error('End date is required');
    if (form.start_date > form.end_date) return toast.error('Start date must be before end date');

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        context_key: form.context_key.trim().toLowerCase().replace(/\s+/g, '-'),
        description: form.description.trim() || null,
        start_date: form.start_date,
        end_date: form.end_date,
        active: form.active ? 1 : 0,
      };
      if (editingId) {
        await api(`/api/admin/festival-schedules/${editingId}`, { method: 'PUT', body: JSON.stringify(payload) });
        toast.success('Schedule updated');
      } else {
        await api('/api/admin/festival-schedules', { method: 'POST', body: JSON.stringify(payload) });
        toast.success('Schedule created');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api(`/api/admin/festival-schedules/${deleteId}`, { method: 'DELETE' });
      toast.success('Deleted');
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom={false}>
            Festival Schedule
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Schedule hero sliders on the <strong>Festive Vibes</strong> page by festival and date range.
            The active festival's slider is shown automatically — no code change needed.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<IoAddOutline />} onClick={openAdd}>
          Add festival
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="My Schedules" icon={<IoCalendarOutline />} iconPosition="start" />
        <Tab label="Festival Calendar 2026–27" icon={<IoCalendarOutline />} iconPosition="start" />
      </Tabs>

      {activeTab === 1 && (
        <FestivalCalendarView schedules={schedules} onSchedulesRefresh={load} />
      )}

      {activeTab === 0 && (
      <>
      {/* Info box */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>How it works:</strong> Create a schedule entry for each festival (e.g. Diwali, Navratri).
        The <em>context key</em> must exactly match a hero slider you create in{' '}
        <Link to="/admin/pages/hero-sliders" style={{ color: 'inherit', fontWeight: 600 }}>Hero Sliders</Link>{' '}
        (under Shop → custom key). On the date range you set, that slider will automatically replace
        the default Festive Vibes hero. Use <strong>Force active</strong> to override without dates.
      </Alert>

      {/* Table */}
      <Card variant="outlined">
        <CardHeader
          avatar={<IoCalendarOutline size={22} />}
          title="Scheduled festivals"
          subheader={`${schedules.length} entr${schedules.length === 1 ? 'y' : 'ies'}`}
        />
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <AdminLoading />
          ) : schedules.length === 0 ? (
            <AdminEmptyState
              icon={IoCalendarOutline}
              message="No festivals scheduled. Add your first festival schedule to automatically swap the Festive Vibes hero slider."
            />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Festival</TableCell>
                    <TableCell>Context key</TableCell>
                    <TableCell>Dates</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Hero slider</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schedules.map((row) => {
                    const live = isCurrentlyActive(row);
                    return (
                      <TableRow key={row.id} hover sx={live ? { backgroundColor: 'rgba(46,125,50,0.06)' } : {}}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                          {row.description && (
                            <Typography variant="caption" color="text.secondary">{row.description}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <code style={{ fontSize: '0.78rem', background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>
                            {row.context_key}
                          </code>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" display="block">{formatDate(row.start_date)}</Typography>
                          <Typography variant="caption" color="text.secondary">to {formatDate(row.end_date)}</Typography>
                        </TableCell>
                        <TableCell>
                          {row.active === 1 || row.active === true ? (
                            <Chip label="Force active" color="warning" size="small" />
                          ) : live ? (
                            <Chip label="Active now" color="success" size="small" />
                          ) : (
                            <Chip label="Scheduled" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Manage hero slider for this festival">
                            <Button
                              component={Link}
                              to="/admin/pages/hero-sliders"
                              size="small"
                              startIcon={<IoImageOutline />}
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            >
                              Set slider
                            </Button>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(row)}>
                              <IoPencilOutline />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => setDeleteId(row.id)}>
                              <IoTrashOutline />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      </> )}

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit festival schedule' : 'Add festival schedule'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {/* Quick suggestions */}
          {!editingId && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                Quick fill:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {FESTIVAL_SUGGESTIONS.map((s) => (
                  <Chip
                    key={s.key}
                    label={s.name}
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => handleSuggestion(s)}
                  />
                ))}
              </Box>
            </Box>
          )}

          <TextField
            label="Festival name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
            fullWidth
            size="small"
            placeholder="e.g. Diwali 2026"
          />

          <TextField
            label="Context key"
            value={form.context_key}
            onChange={(e) => setForm((f) => ({ ...f, context_key: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
            required
            fullWidth
            size="small"
            placeholder="e.g. diwali"
            helperText="Must exactly match the hero slider key you create in Hero Sliders → Shop. Use lowercase and hyphens."
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Start date"
              type="date"
              value={form.start_date}
              onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
              required
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End date"
              type="date"
              value={form.end_date}
              onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
              required
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <TextField
            label="Description (optional)"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            fullWidth
            size="small"
            multiline
            rows={2}
            placeholder="Internal note about this schedule"
          />

          <FormControlLabel
            control={
              <Switch
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                color="warning"
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={500}>Force active</Typography>
                <Typography variant="caption" color="text.secondary">
                  Override dates — this slider will always be shown regardless of today's date.
                </Typography>
              </Box>
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editingId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete festival schedule?</DialogTitle>
        <DialogContent>
          <Typography>This will remove the schedule. The hero slider data in Hero Sliders will not be deleted.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
